import React, { useEffect, useState } from "react";
import { getAppointmentSlots, submitContactRequest } from "@quaweb/service-proxies";
import type { SlotWeek } from "./slot-types";
import type { ContactRequestContentProps } from "../../content/component-contact-request";
import { trackCustomEvent, trackLeadConversion } from "../../utils/tag-manager";
import { SlotPicker } from "./SlotPicker";

export type ContactRequestFormProps = {
    content: ContactRequestContentProps;
    /**
     * Worum es geht — von der aufrufenden Sektion vorbelegt, nicht getippt.
     * Wird angezeigt, damit der Besucher sieht, was mitgeschickt wird, ist aber
     * kein Eingabefeld (Entscheid C5).
     */
    topic: string;
    /** Ansprechpartner; Standard ist Michael, jede Seite kann ihn ersetzen. */
    recipientEmail?: string;
    recipientName?: string;
    /** Wessen Kalender die freien Zeiten liefert (Entscheid C5). */
    calendarId?: string;
    /**
     * Kennung der Kampagnenseite, z.B. `ausschreibungen`. Das Formular meldet
     * sie als `campaign_page` an der Conversion `contact_request` (QUAWEB-42).
     */
    campaignPage?: string;
    language: "de" | "en";
    onSuccess: () => void;
    onCancel: () => void;
};

type FormState = {
    name: string;
    email: string;
    message: string;
    company: string;
    phone: string;
    preferredTime: string;
};

/**
 * Welchen Weg der Besucher gewaehlt hat (Feedback Jan, 3.8.2026).
 *
 * Das Formular fragte bisher alles auf einmal ab — Termin UND Nachricht, beides
 * sichtbar. Jetzt zwei Wege: der Termin traegt die Absicht, oder die Nachricht.
 */
type ContactMode = "appointment" | "message";

/**
 * Fehler haengen meist an einem Eingabefeld; die Slot-Auswahl ist keins.
 * Deshalb ein eigener Schluessel neben den Formularfeldern.
 */
type ErrorKey = keyof FormState | "slot";

const EMPTY_FORM: FormState = {
    name: "",
    email: "",
    message: "",
    company: "",
    phone: "",
    preferredTime: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Der gewaehlte Termin im Klartext, in der Zone des Kalenders.
 *
 * Nicht in der Browser-Zone: ein Besucher in London soll dieselbe Uhrzeit
 * lesen wie einer in Zuerich, sonst bucht er 09:00 und erscheint um 08:00.
 */
const formatSlot = (iso: string, language: "de" | "en", timeZone: string): string =>
    new Intl.DateTimeFormat(language === "en" ? "en-GB" : "de-CH", {
        timeZone,
        dateStyle: "full",
        timeStyle: "short",
    }).format(new Date(iso));

const FIELD_CLASS =
    "block w-full rounded-md border border-gray-300 px-3 py-2 text-base text-gray-900 " +
    "focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:bg-gray-100";

export const ContactRequestForm: React.FC<ContactRequestFormProps> = ({
    content,
    topic,
    recipientEmail,
    recipientName,
    calendarId,
    campaignPage,
    language,
    onSuccess,
    onCancel,
}) => {
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [mode, setMode] = useState<ContactMode>("appointment");
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<ErrorKey, string>>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [weeks, setWeeks] = useState<SlotWeek[]>([]);
    const [timeZone, setTimeZone] = useState("Europe/Zurich");
    const [calendarAvailable, setCalendarAvailable] = useState<boolean | null>(null);
    const [slot, setSlot] = useState<{ start: string; end: string } | null>(null);

    // Slots erst beim Oeffnen holen, nicht beim Bauen der Seite: sie veralten
    // binnen Minuten, und eine statisch gebaute Liste zeigte Termine an, die
    // laengst vergeben sind.
    useEffect(() => {
        let cancelled = false;
        void getAppointmentSlots({ calendarId })
            .then(result => {
                if (cancelled) {
                    return;
                }
                setWeeks(result.weeks);
                setTimeZone(result.timeZone);
                setCalendarAvailable(result.calendarAvailable);
            })
            .catch(error => {
                // Kein Abbruch: ohne Kalender bleibt das Formular als
                // Nachrichtenweg vollstaendig benutzbar.
                console.error("Failed to load appointment slots:", error);
                if (!cancelled) {
                    setCalendarAvailable(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [calendarId]);

    /**
     * Echte Slots oder Freitext?
     *
     * `weeks.length > 0` statt `calendarAvailable`: es deckt auch den Fall ab,
     * dass der Kalender antwortet, aber keine freien Zeiten liefert — dann gibt
     * es nichts auszuwaehlen, und der Wunschtermin uebernimmt. Dieselbe
     * Bedingung steuert weiter unten die Anzeige, damit Pruefung und Darstellung
     * nicht auseinanderlaufen koennen.
     */
    const usesSlotPicker = weeks.length > 0;

    const handleChange =
        (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { value } = event.target;
            setForm(prev => ({ ...prev, [field]: value }));
            // Fehler verschwindet beim Tippen, nicht erst beim naechsten Absenden.
            setFieldErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
        };

    /**
     * Was Pflicht ist, haengt vom gewaehlten Weg ab (Feedback Jan, 3.8.2026:
     * «hat zu viele Felder»).
     *
     * - **Termin:** der Termin traegt die Absicht, die Nachricht ist freiwillig.
     *   Pflicht ist der Slot — oder, wenn der Kalender nicht erreichbar war, der
     *   Wunschtermin als Freitext. Ohne beides waere die Anfrage inhaltsleer.
     * - **Nachricht:** umgekehrt — die Nachricht ist Pflicht, ein Termin wird
     *   gar nicht erst gefragt.
     *
     * Name und E-Mail sind immer Pflicht, Firma und Telefon nie.
     */
    const validate = (): boolean => {
        const errors: Partial<Record<ErrorKey, string>> = {};

        (["name", "email"] as const).forEach(field => {
            if (!form[field].trim()) {
                errors[field] = content.requiredField;
            }
        });
        if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) {
            errors.email = content.invalidEmail;
        }

        if (mode === "message") {
            if (!form.message.trim()) {
                errors.message = content.requiredField;
            }
        } else if (usesSlotPicker) {
            if (!slot) {
                errors.slot = content.slotRequired;
            }
        } else if (!form.preferredTime.trim()) {
            // Kalender nicht erreichbar: dann traegt der Wunschtermin die Absicht.
            errors.preferredTime = content.requiredField;
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isSubmitting || !validate()) {
            return;
        }
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            await submitContactRequest({
                topic,
                name: form.name.trim(),
                email: form.email.trim(),
                message: form.message.trim(),
                company: form.company.trim() || undefined,
                phone: form.phone.trim() || undefined,
                // Wer den Weg wechselt, laesst Eingaben des anderen im Zustand
                // zurueck. Ungefiltert ginge ein Termin mit, den der Besucher
                // im Nachrichten-Modus gar nicht mehr sieht — die Bestaetigung
                // versprach dann einen Termin, den niemand gewaehlt hat.
                preferredTime: (mode === "appointment" && form.preferredTime.trim()) || undefined,
                slotStart: mode === "appointment" ? slot?.start : undefined,
                slotEnd: mode === "appointment" ? slot?.end : undefined,
                recipientEmail,
                recipientName,
                language,
            });
            trackCustomEvent("contact_request_submitted", {
                topic,
                mode,
                with_slot: mode === "appointment" && Boolean(slot),
                with_preferred_time: mode === "appointment" && form.preferredTime.trim().length > 0,
            });
            trackLeadConversion("contact_request", { campaignPage });
            onSuccess();
        } catch (error) {
            console.error("Failed to submit contact request:", error);
            setErrorMessage(content.errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderError = (field: ErrorKey) =>
        fieldErrors[field] ? (
            <p id={`contact-request-${field}-error`} className="mt-1 text-sm text-red-600">
                {fieldErrors[field]}
            </p>
        ) : null;

    const fieldProps = (field: keyof FormState) => ({
        id: `contact-request-${field}`,
        value: form[field],
        onChange: handleChange(field),
        disabled: isSubmitting,
        className: FIELD_CLASS,
        "aria-invalid": Boolean(fieldErrors[field]),
        "aria-describedby": fieldErrors[field] ? `contact-request-${field}-error` : undefined,
    });

    return (
        <form
            onSubmit={event => {
                void handleSubmit(event);
            }}
            className="space-y-4"
            noValidate
        >
            <p className="text-gray-600">{content.intro}</p>

            {/*
                Das Thema kommt aus dem Content der Seite, nicht aus einer Eingabe.
                Sichtbar, damit der Besucher weiss, was mitgeht — aber nicht
                editierbar, denn die Seite weiss es besser als er.
            */}
            <div className="rounded-md bg-gray-50 px-3 py-2">
                <span className="block text-sm font-medium text-gray-500">{content.topicLabel}</span>
                <span className="block text-base font-semibold text-primary">{topic}</span>
            </div>

            {/*
                Umschaltung zwischen den beiden Wegen (Feedback Jan, 3.8.2026:
                «hat zu viele Felder»).

                `radiogroup` statt Tabs im ARIA-Sinn: Tabs verwalten Panels, die
                unabhaengig voneinander existieren — hier ist es EIN Formular,
                dessen Pflichtfelder sich aendern. Ein Radio-Paar sagt genau das,
                und die Pfeiltasten funktionieren ohne Zusatzcode.
            */}
            <fieldset className="border-0 p-0 m-0">
                <legend className="sr-only">{content.modeLegend}</legend>
                <div className="grid grid-cols-2 gap-2 rounded-md bg-gray-100 p-1">
                    {(
                        [
                            ["appointment", content.modeAppointment],
                            ["message", content.modeMessage],
                        ] as const
                    ).map(([value, label]) => (
                        <label
                            key={value}
                            className={`cursor-pointer rounded px-3 py-2 text-center text-sm font-semibold transition-colors ${
                                mode === value ? "bg-white text-primary shadow-sm" : "text-gray-600 hover:text-gray-900"
                            }`}
                        >
                            <input
                                type="radio"
                                name="contact-request-mode"
                                value={value}
                                checked={mode === value}
                                disabled={isSubmitting}
                                onChange={() => {
                                    setMode(value);
                                    // Fehler des anderen Weges stehen lassen waere
                                    // verwirrend: sie gelten hier nicht mehr.
                                    setFieldErrors({});
                                }}
                                className="sr-only"
                            />
                            {label}
                        </label>
                    ))}
                </div>
            </fieldset>

            <div>
                <label htmlFor="contact-request-name" className="block text-sm font-medium text-gray-700 mb-1">
                    {content.name}
                </label>
                <input type="text" autoComplete="name" {...fieldProps("name")} />
                {renderError("name")}
            </div>

            <div>
                <label htmlFor="contact-request-email" className="block text-sm font-medium text-gray-700 mb-1">
                    {content.email}
                </label>
                <input type="email" autoComplete="email" {...fieldProps("email")} />
                {renderError("email")}
            </div>

            <div>
                <label htmlFor="contact-request-message" className="block text-sm font-medium text-gray-700 mb-1">
                    {mode === "appointment" ? content.messageOptional : content.message}
                </label>
                <textarea rows={4} placeholder={content.messagePlaceholder} {...fieldProps("message")} />
                {renderError("message")}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="contact-request-company" className="block text-sm font-medium text-gray-700 mb-1">
                        {content.company}
                    </label>
                    <input type="text" autoComplete="organization" {...fieldProps("company")} />
                </div>
                <div>
                    <label htmlFor="contact-request-phone" className="block text-sm font-medium text-gray-700 mb-1">
                        {content.phone}
                    </label>
                    <input type="tel" autoComplete="tel" {...fieldProps("phone")} />
                </div>
            </div>

            {/*
                Echte Slots sind der Normalfall; das Freitextfeld erscheint NUR,
                wenn der Kalender nicht erreichbar war (Entscheid Jan). Beides
                gleichzeitig zu zeigen waere eine doppelte Frage nach derselben
                Sache.

                `calendarAvailable === null` heisst «wird noch geladen» — dann
                steht hier nichts, statt kurz das Rueckfallfeld aufblitzen zu
                lassen.
            */}
            {mode === "appointment" && usesSlotPicker && (
                <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">{content.chooseSlot}</span>
                    <SlotPicker
                        content={content}
                        weeks={weeks}
                        language={language}
                        timeZone={timeZone}
                        selected={slot?.start ?? null}
                        onSelect={setSlot}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                        {slot
                            ? `${content.slotSelected} ${formatSlot(slot.start, language, timeZone)}`
                            : content.slotOptional}
                    </p>
                    {renderError("slot")}
                </div>
            )}

            {mode === "appointment" && !usesSlotPicker && calendarAvailable !== null && (
                <div>
                    <label
                        htmlFor="contact-request-preferredTime"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        {content.preferredTimeRequired}
                    </label>
                    <input
                        type="text"
                        placeholder={content.preferredTimePlaceholder}
                        {...fieldProps("preferredTime")}
                    />
                    {renderError("preferredTime")}
                </div>
            )}

            {errorMessage && (
                <p role="alert" className="text-sm text-red-600">
                    {errorMessage}
                </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    // eslint-disable-next-line max-len
                    className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-dark disabled:opacity-60"
                >
                    {isSubmitting ? content.submitting : content.submit}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    // eslint-disable-next-line max-len
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 px-5 py-3 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
                >
                    {content.cancel}
                </button>
            </div>
        </form>
    );
};
